import { useState, useEffect } from 'react';
import { getLoginState } from 'model/user';
import repo from 'service/repository';
import mqtt from 'service/mqtt';
import { INIT_APP_DONE, SHOW_APP_MESSAGE, UPDATE_BOOTSTRAPS } from 'use-app';
import { getUserMobile, saveUserMobile } from 'service/local-data-provider';
import { isPhoneNumber } from 'helpers/rule-validation';
import qs from 'query-string';
import { isAlpha, PATH } from 'config';
import { CHANGE_MOBILE } from 'feature/login-flow/use-login-flow';
import { connectMQTT } from 'helpers/connect-mqtt';

const useInit = () => {
  const { mobile } = qs.parse(window.location.search);
  const [isPending, setIsPending] = useState(true);

  useEffect(() => {
    return () => {
      mqtt.disconnect();
    };
  }, []);

  useEffect(() => {
    let userMobile = '';
    if (isAlpha) {
      const currentMobile = getUserMobile();
      userMobile = currentMobile || mobile || '';
    } else {
      userMobile = mobile || '';
    }

    saveUserMobile(userMobile);
    setIsPending(false);
  }, [mobile]);

  return { isPending };
};

const loginAsUser = (success, failure) => {
  return Promise.all([
    repo.fetchUserInfo(),
    repo.fetchLicensePlates(),
    repo.fetchBootstraps(),
    repo.fetchParkingRecord(),
    repo.fetchPromotionBoard(),
    repo.fetchOrders(1, 100, ['refueling']),
    repo.fetchOrders(1, 100, ['parking']),
    repo.updatePushToken(getUserMobile()),
    repo.agreeTermsOfTaipeiParkinglotService(),
  ])
    .then(data => {
      const payload = {
        me: data[0],
        licensePlates: data[1],
        bootstraps: data[2],
        parkingRecord: data[3],
        promotionBoard: data[4],
        orders: {
          refueling: data[5].data,
          parking: data[6].data,
        },
      };

      success(payload);
    })
    .catch(e => {
      failure(e);
    });
};

const useSplash = async ({ state, dispatch, history, location }, callback) => {
  const waitForSplashAnimationComplete = fn => {
    return new Promise(resolve => {
      resolve(callback());
    }).then(() => {
      setTimeout(() => fn(), 2200);
    });
  };

  const initAppWithoutPayload = () => dispatch({ type: INIT_APP_DONE });

  await repo.fetchBootstraps().then(payload => {
    dispatch({
      type: UPDATE_BOOTSTRAPS,
      payload,
    });
  });

  // 如果沒有登入又沒有 user mobile 需要彈出錯誤訊 息請使用者重新進入 app
  // 目前已知會走這條入的情境是推播
  if (!isPhoneNumber(getUserMobile()) && getLoginState() !== 'user') {
    waitForSplashAnimationComplete(() => {
      dispatch({
        type: SHOW_APP_MESSAGE,
        payload: {
          type: 'failure',
          message: '無法取得你的使用者資訊，請關閉麻吉付後重啟打開麻吉付。',
        },
      });
    });
  }

  if (location.pathname === PATH.MAPS || location.pathname === PATH.SERVICE_INSTRUCTION) {
    return waitForSplashAnimationComplete(initAppWithoutPayload);
  }

  switch (getLoginState()) {
    case 'user':
      const success = payload => {
        waitForSplashAnimationComplete(() => {
          dispatch({
            type: INIT_APP_DONE,
            payload: payload,
          });
          connectMQTT({ state, dispatch });

          if (location.pathname === '/' || location.pathname === '/index.html') {
            history.replace(PATH.MAIN);
          } else {
            history.replace(`${location.pathname}${location.search}${location.hash}`);
          }
        });
      };

      const failure = () => {
        waitForSplashAnimationComplete(() => {
          initAppWithoutPayload();
          history.replace(PATH.USER_WELCOME);
        });
      };

      loginAsUser(success, failure);

      break;
    case 'skip':
      waitForSplashAnimationComplete(() => {
        initAppWithoutPayload();
        if (location.pathname === '/') {
          history.replace(PATH.MAIN);
        } else {
          history.replace(`${location.pathname}${location.search}`);
        }
      });
      break;
    default:
      waitForSplashAnimationComplete(() => {
        initAppWithoutPayload();
        if (location.pathname !== PATH.USER_LOGIN) {
          history.replace(PATH.USER_WELCOME);
        }
      });
      break;
  }
};

const preLogin = dispatch => {
  dispatch({ type: CHANGE_MOBILE, payload: getUserMobile() });
};

const methods = {
  useInit,
  useSplash,
  preLogin,
  loginAsUser,
};

export default methods;
